import axios from "axios";

const inferApiUrl = () => {
  const explicit = process.env.REACT_APP_API_URL;
  if (explicit) {
    return explicit.replace(/\/$/, "");
  }

  if (typeof window !== "undefined" && window.location) {
    const { protocol, hostname, port } = window.location;
    const currentPort = port || (protocol === "https:" ? "443" : "80");
    const inferredPort =
      currentPort === "3000" || currentPort === "3001" ? "8000" : currentPort;
    return `${protocol}//${hostname}:${inferredPort}`.replace(/\/$/, "");
  }

  return "http://localhost:8000";
};

const API_URL = inferApiUrl();

export const API_BASE_URL = API_URL;

const notifyAuthUpdate = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("auth-update"));
  }
};

export const resolveWsUrl = (path = "/ws/prices") => {
  const explicit = process.env.REACT_APP_WS_URL;
  if (explicit) {
    try {
      const base = explicit.endsWith("/") ? explicit : `${explicit}/`;
      const url = new URL(base);
      url.pathname = path.startsWith("/") ? path : `/${path}`;
      url.search = "";
      url.hash = "";
      return url.toString();
    } catch (err) {
      console.warn(
        "Invalid REACT_APP_WS_URL, falling back to string concat",
        err
      );
      return `${explicit.replace(/\/$/, "")}${path}`;
    }
  }

  try {
    const url = new URL(API_URL);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.pathname = path;
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch (err) {
    console.warn(
      "Unable to derive WebSocket URL from API_URL, falling back to default",
      err
    );
    return `ws://localhost:8000${path}`;
  }
};

// Initialize token from localStorage
let token = localStorage.getItem("token");

// Set token globally and persist to localStorage
export const setToken = (t) => {
  token = t;
  if (t) {
    localStorage.setItem("token", t);
  } else {
    localStorage.removeItem("token");
  }
  notifyAuthUpdate();
};

export const getToken = () => token;

// Set role in localStorage
export const setRole = (role) => {
  if (role) {
    localStorage.setItem("role", role);
  } else {
    localStorage.removeItem("role");
  }
  notifyAuthUpdate();
};

export const getRole = () => localStorage.getItem("role");

// Clear all auth data
export const clearAuth = () => {
  token = null;
  localStorage.removeItem("token");
  localStorage.removeItem("role");
  localStorage.removeItem("username");
  localStorage.removeItem("email");
  notifyAuthUpdate();
};

export const getStoredEmail = () => localStorage.getItem("email");

// ---------------------- Signup ----------------------
export const signup = async (username, email, password, otp) => {
  const payload = { username, email };
  if (password !== undefined && password !== null) {
    payload.password = password;
  }
  if (otp) {
    payload.otp = otp;
  }
  const res = await axios.post(`${API_URL}/auth/signup`, payload);
  return res.data;
};

// ---------------------- Login ----------------------
export const login = async (username, password) => {
  // FastAPI expects x-www-form-urlencoded data
  const formData = new URLSearchParams();
  formData.append("username", username);
  formData.append("password", password);

  const res = await axios.post(`${API_URL}/auth/login`, formData, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });

  // Store authentication data
  setToken(res.data.access_token);
  setRole(res.data.role);
  localStorage.setItem("username", res.data.username);
  if (res.data.email) {
    localStorage.setItem("email", res.data.email);
  } else {
    localStorage.removeItem("email");
  }

  return res.data;
};

// ---------------------- User Profile ----------------------
export const getProfile = async () => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.get(`${API_URL}/user/profile`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const requestBalanceChange = async (requestType, amount) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/user/balance/request`,
    { amount, request_type: requestType },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const forgotPassword = async (email) => {
  const res = await axios.post(`${API_URL}/auth/password/forgot`, { email });
  return res.data;
};

export const resetPassword = async ({ email, otp, newPassword }) => {
  const payload = { new_password: newPassword };
  if (otp) {
    payload.otp = otp;
  }
  if (email) {
    payload.email = email;
  }
  const res = await axios.post(`${API_URL}/auth/password/reset`, payload);
  return res.data;
};

export const getUserHistory = async (params = {}) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.get(`${API_URL}/user/history`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return res.data;
};

// ---------------------- Admin: User History Summary ----------------------
export const getUserHistoryAdmin = async (userId, params = {}) => {
  if (!token) throw new Error("No token set. Login first.");
  // Re-use user history endpoint by impersonating via token of the user is not available.
  // Backend lacks an admin-scoped endpoint, so we will fall back to fetching per user via a dedicated admin endpoint if available in future.
  const res = await axios.get(`${API_URL}/admin/users/${userId}/history`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return res.data;
};

// ---------------------- Place Order ----------------------
export const placeOrder = async (symbol, side, qty) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/orders`,
    { symbol, side, qty, order_type: "market" },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// ---------------------- Get Positions ----------------------
export const getPositions = async () => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.get(`${API_URL}/positions`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// ---------------------- Admin: Get All Users ----------------------
export const getAllUsers = async () => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.get(`${API_URL}/admin/users`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const getBalanceRequestsAdmin = async (status) => {
  if (!token) throw new Error("No token set. Login first.");
  const params = status ? { status } : undefined;
  const res = await axios.get(`${API_URL}/admin/balance/requests`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return res.data;
};

export const decideBalanceRequestAdmin = async (
  requestId,
  action,
  adminComment = "",
  adjustedAmount = null
) => {
  if (!token) throw new Error("No token set. Login first.");
  const payload = { action, admin_comment: adminComment };
  if (adjustedAmount !== null && adjustedAmount !== undefined) {
    payload.adjusted_amount = adjustedAmount;
  }
  const res = await axios.post(
    `${API_URL}/admin/balance/requests/${requestId}/decision`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// ---------------------- Admin: Manage Positions ----------------------
export const getUserPositionsAdmin = async (userId) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.get(`${API_URL}/admin/users/${userId}/positions`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const updateUserPositionAdmin = async (positionId, updates) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.put(
    `${API_URL}/admin/positions/${positionId}`,
    updates,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return res.data;
};

export const closeUserPositionAdmin = async (positionId, payload = {}) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/positions/${positionId}/close`,
    payload,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return res.data;
};

export const exitAllPositionsAdmin = async (userId, options = {}) => {
  if (!token) throw new Error("No token set. Login first.");
  const payload = {};
  if (options.symbol) {
    payload.symbol = options.symbol;
  }
  if (options.slippage !== undefined && options.slippage !== null) {
    payload.slippage = options.slippage;
  }
  const res = await axios.post(
    `${API_URL}/admin/users/${userId}/positions/exit-all`,
    payload,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return res.data;
};

export const createUserPositionAdmin = async (userId, payload) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/users/${userId}/positions`,
    payload,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return res.data;
};

export const toggleUserBotAdmin = async (userId, active) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/users/${userId}/bot`,
    { active },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const adminCreateUser = async ({ username, password, email }) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/users`,
    { username, password, email },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Admin-set password for a user (fallback while password-reset emails are down).
// Never sends an email - the admin hands the password over out of band.
export const adminResetUserPassword = async (
  userId,
  newPassword,
  forceLogout = false
) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/users/${userId}/reset-password`,
    { new_password: newPassword, force_logout: forceLogout },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const adminDeleteUser = async (userId) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.delete(`${API_URL}/admin/users/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const updateUserTradeSettingsAdmin = async (userId, defaultTradeQty) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.put(
    `${API_URL}/admin/users/${userId}/trade-settings`,
    { default_trade_qty: defaultTradeQty },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// ---------------------- Admin: Grid Settings ----------------------
export const getUserGridSettingsAdmin = async (userId) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.get(
    `${API_URL}/admin/users/${userId}/grid-settings`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return res.data;
};

export const updateUserGridSettingsAdmin = async (userId, payload) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.put(
    `${API_URL}/admin/users/${userId}/grid-settings`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const resetUserGridSettingsAdmin = async (userId) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.delete(
    `${API_URL}/admin/users/${userId}/grid-settings`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return res.data;
};

export const getOptionExpiriesAdmin = async () => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.get(`${API_URL}/options/expiries`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const getOptionStrikesAdmin = async (expiry, optionType) => {
  if (!token) throw new Error("No token set. Login first.");
  const params = { expiry, option_type: optionType };
  const res = await axios.get(`${API_URL}/options/strikes`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return res.data;
};

export const getOptionQuoteAdmin = async (expiry, strike, optionType) => {
  if (!token) throw new Error("No token set. Login first.");
  const params = { expiry, strike, option_type: optionType };
  const res = await axios.get(`${API_URL}/options/price`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return res.data;
};

// ---------------------- Admin: System Health ----------------------
export const getAdminHealthStatus = async () => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.get(`${API_URL}/admin/health/status`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const getAdminSystemHealth = async () => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.get(`${API_URL}/admin/health/system`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// ---------------------- Admin: Manual Position ----------------------
export const openManualPositionAdmin = async (
  userId,
  symbol,
  side,
  takeProfit,
  qty,
  notes,
  entryPrice = null
) => {
  if (!token) throw new Error("No token set. Login first.");
  const payload = { symbol, side, take_profit: takeProfit, qty, notes };
  // Feature 4: Include entry_price if provided
  if (entryPrice !== null && entryPrice > 0) {
    payload.entry_price = entryPrice;
  }
  const res = await axios.post(
    `${API_URL}/admin/users/${userId}/manual-position`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// ---------------------- Admin: SPX500 Options Trade ----------------------
export const executeSpxOptionsTrade = async (payload) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(`${API_URL}/admin/spx-options-trade`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// ---------------------- Admin: SPX Multipliers ----------------------
export const updateSpxMultipliersBulk = async (multipliers) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.put(`${API_URL}/admin/spx-multipliers/bulk`, { multipliers }, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// ---------------------- Admin: Manual Swap ----------------------
export const applyManualSwapAdmin = async (userId, payload) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/users/${userId}/manual-swap`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// ---------------------- Admin: Global Bot Control ----------------------
export const getGlobalBotStatus = async () => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.get(`${API_URL}/admin/bots/status`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const pauseAllBotEntries = async () => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/bots/pause-entries`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const resumeAllBotEntries = async () => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/bots/resume-entries`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const exitAllPositionsGlobal = async () => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/bots/exit-all-positions`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// ---------------------- Admin: Ledger Report CSV ----------------------
export const downloadLedgerCsvAdmin = async (
  userId = null,
  startDate = null,
  endDate = null
) => {
  if (!token) throw new Error("No token set. Login first.");
  const params = {};
  if (userId) params.user_id = userId;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;

  const res = await axios.get(`${API_URL}/admin/reports/ledger.csv`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
    responseType: "blob",
  });

  // Create download link
  const blob = new Blob([res.data], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;

  // Extract filename from Content-Disposition header or generate one
  const contentDisposition = res.headers["content-disposition"];
  let filename = "ledger.csv";
  if (contentDisposition) {
    const match = contentDisposition.match(/filename=([^;]+)/);
    if (match) filename = match[1].replace(/"/g, "");
  }
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { success: true, filename };
};

// ---------------------- Admin: Withdrawal Management ----------------------
export const initiateAdminWithdrawal = async (userId, amount, note = null) => {
  if (!token) throw new Error("No token set. Login first.");
  const payload = { user_id: userId, amount };
  if (note) payload.note = note;
  const res = await axios.post(
    `${API_URL}/admin/withdrawal/initiate`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const getAdminWithdrawalRequests = async (
  status = null,
  userId = null
) => {
  if (!token) throw new Error("No token set. Login first.");
  const params = {};
  if (status) params.status = status;
  if (userId) params.user_id = userId;
  const res = await axios.get(`${API_URL}/admin/withdrawal/requests`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return res.data;
};

export const cancelAdminWithdrawal = async (requestId) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/withdrawal/${requestId}/cancel`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Public: Process withdrawal (approve/reject) - no auth needed
export const processWithdrawalAction = async (withdrawalToken, action) => {
  const res = await axios.get(
    `${API_URL}/withdrawal/${action}/${withdrawalToken}`
  );
  return res.data;
};

// ---------------------- Admin: Daily PnL CSV ----------------------
export const downloadDailyPnlCsvAdmin = async (date = null) => {
  if (!token) throw new Error("No token set. Login first.");
  const params = {};
  if (date) params.date = date;

  const res = await axios.get(`${API_URL}/admin/reports/daily-pnl.csv`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
    responseType: "blob",
  });

  const blob = new Blob([res.data], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;

  const contentDisposition = res.headers["content-disposition"];
  let filename = "daily-pnl.csv";
  if (contentDisposition) {
    const match = contentDisposition.match(/filename=([^;]+)/);
    if (match) filename = match[1].replace(/"/g, "");
  }
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { success: true, filename };
};

// ---------------------- Admin: User Transaction Report CSV ----------------------
export const downloadUserTransactionReport = async (userId, startDate, endDate) => {
  if (!token) throw new Error("No token set. Login first.");

  const params = {
    start_date: startDate,
    end_date: endDate,
  };

  const res = await axios.get(
    `${API_URL}/admin/users/${userId}/transaction-report`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params,
      responseType: "blob",
    }
  );

  // Create download link
  const blob = new Blob([res.data], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;

  // Extract filename from Content-Disposition header or generate one
  const contentDisposition = res.headers["content-disposition"];
  let filename = `transaction_report_${userId}.csv`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^";]+)"?/);
    if (match) filename = match[1].replace(/"/g, "");
  }
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { success: true, filename };
};

// ---------------------- Admin: Multi-Bot Control ----------------------

// Get all bot configurations for a user
export const getUserBotsAdmin = async (userId) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.get(`${API_URL}/admin/users/${userId}/bots`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// Update bot configuration for a specific symbol
export const updateUserBotAdmin = async (userId, symbol, payload) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.put(
    `${API_URL}/admin/users/${userId}/bots/${symbol}`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Start a specific bot for a user
export const startUserBotAdmin = async (userId, symbol) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/users/${userId}/bots/${symbol}/start`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Stop a specific bot for a user
export const stopUserBotAdmin = async (userId, symbol) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/users/${userId}/bots/${symbol}/stop`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Stop all bots for a user
export const stopAllUserBotsAdmin = async (userId) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/users/${userId}/bots/stop-all`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// ---------------------- Admin: Manual Gold (XAUUSD) Batch Entry ----------------------

// List all saved gold user-lists (summary)
export const getGoldLists = async () => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.get(`${API_URL}/admin/gold-lists`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// Get one list with its members
export const getGoldList = async (listId) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.get(`${API_URL}/admin/gold-lists/${listId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// Create a list: { name, members: [{ user_id, qty, slippage, swap }] }
export const createGoldList = async (payload) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(`${API_URL}/admin/gold-lists`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// Update a list: { name?, members? }
export const updateGoldList = async (listId, payload) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.put(`${API_URL}/admin/gold-lists/${listId}`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// Delete a list
export const deleteGoldList = async (listId) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.delete(`${API_URL}/admin/gold-lists/${listId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// Preview a batch (no writes): { side, entry_price, exit_price }
export const previewGoldBatch = async (listId, payload) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/gold-lists/${listId}/preview`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Execute a batch: { side, entry_price, exit_price, trade_date?, notes?, idempotency_key }
export const executeGoldBatch = async (listId, payload) => {
  if (!token) throw new Error("No token set. Login first.");
  const res = await axios.post(
    `${API_URL}/admin/gold-lists/${listId}/execute`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};
